import { BaseServer, BaseServerConfig } from '../BaseServer.js';
import { MASTER_AREA_PROTOCOLS, SERVER_TYPES } from '../constants.js';
import type { DynamicAreaRecord } from './DynamicAreaTable.js';
import type { AreaHostLimits } from './AreaHost.js';

/**
 * What the agent reports to the master about this host — its registered area
 * types plus every live instance (full record shape so a restarted master can
 * adopt them verbatim).
 */
export type HostReport = {
  registeredTypes: string[];
  typeCapacities?: { [type: string]: number };
  /** Per-type default instance policy this host's types declare. */
  typeDefaults?: { [type: string]: { emptyTtlMs?: number; maxClients?: number } };
  /** Host-level totals from this process's config (`areaHost` block). */
  limits?: AreaHostLimits;
  instances: DynamicAreaRecord[];
};

/**
 * The process-local execution half the agent delegates to (implemented by the
 * Starter): actually construct/tear down instance AreaServers and describe the
 * host's current state.
 */
export type RemoteAreaExecutor = {
  spawn(record: DynamicAreaRecord): Promise<{ port: number }>;
  destroy(areaId: string, drain?: boolean): Promise<void>;
  report(): HostReport;
  /**
   * Re-establish live instances' AREA registrations with a (re)connected master
   * — after a master restart the adopted records need their data-plane link
   * (membership reports, reaping, dispatch) rebuilt.
   */
  reconnectAreas?(masterConfig: BaseServerConfig<any>): Promise<void>;
};

export type AreaHostAgentConfig = Omit<BaseServerConfig<{}>, 'type' | 'options'> & {
  options?: {};
};

/**
 * A lightweight SERVER_TYPES.HOST server that represents a remote process able
 * to spawn dynamic area instances. It registers with the master over the normal
 * ZMQ registration handshake, answers SPAWN_AREA / TEARDOWN_AREA
 * requests by delegating to its executor, and (re)reports its live instances +
 * registered types so the master's registry/table stay reconcilable.
 */
export class AreaHostAgent extends BaseServer {
  private masterId?: string | number;
  private masterRegistration?: BaseServerConfig<any>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private stopped = false;
  // How long to wait between master re-registration attempts after a lost link.
  public reconnectDelayMs = 2000;

  constructor(c: AreaHostAgentConfig, private readonly executor: RemoteAreaExecutor) {
    super({ ...c, options: c.options ?? {}, type: SERVER_TYPES.HOST });
    this.onConnectionMade((d) => {
      if (d.type === SERVER_TYPES.MASTER) {
        this.masterId = d.id;
        // Announce types + live instances as soon as the master link is up —
        // this is both the initial capability announcement and, on a master
        // restart, the reconciliation report it rebuilds its table from.
        this.reportToMaster();
        // ...and rebuild live instances' own AREA registrations to this master
        // so membership/reaping/dispatch keep working after a master restart.
        this.executor.reconnectAreas?.(d)?.catch(err => {
          console.error(`Instance host ${this.id} failed re-registering instances to master ${d.id}: ${err instanceof Error ? err.message : String(err)}`);
        });
      }
    });
    this.onConnectionClosed((d) => {
      // Lost the master (partition / restart / missed heartbeats): keep retrying
      // registration until it answers — hosts must rejoin on their own.
      if (d.type === SERVER_TYPES.MASTER) {
        if (this.masterId === d.id) {
          this.masterId = undefined;
        }
        this.scheduleMasterReconnect();
      }
    });
  }

  /**
   * Register with the master AND remember its config so a lost link is
   * automatically re-established (retrying every `reconnectDelayMs`).
   * The first attempt's failure rejects (boot should be loud); later
   * disconnects retry silently in the background.
   */
  public async connectToMaster(masterConfig: BaseServerConfig<any>): Promise<void> {
    this.masterRegistration = masterConfig;
    await this.sendRegistrationRequest(masterConfig);
  }

  // Set true whenever the reconnect loop actually issues a re-registration —
  // observable proof the mechanism engaged (used by tests / ops introspection).
  public reconnectAttempts = 0;

  private scheduleMasterReconnect() {
    if (this.stopped || !this.masterRegistration || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = undefined;
      if (this.stopped || this.masterId !== undefined || !this.masterRegistration) return;
      try {
        // Drop any stale half-open entry for the master id before re-registering.
        const stale = this.serverConnections[this.masterRegistration.id];
        if (stale) {
          await this.disconnectServer(stale);
        }
        this.reconnectAttempts++;
        await this.sendRegistrationRequest(this.masterRegistration);
      } catch {
        this.scheduleMasterReconnect();
      }
    }, this.reconnectDelayMs);
    (this.reconnectTimer as any).unref?.();
  }

  public override async close() {
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    await super.close();
  }

  public override onHeartbeat() {
    return this.executor.report();
  }

  /** The master this agent registered with (undefined until connected). */
  public get connectedMasterId(): string | number | undefined {
    return this.masterId;
  }

  /** Request the connected master (throws before the master link is up). */
  public requestMaster(payload: any): Promise<any> {
    if (this.masterId === undefined) {
      throw new Error(`Instance host agent ${this.id} is not connected to a master.`);
    }
    return this.requestServer(this.masterId, payload);
  }

  /** Push the current host report to the master (types + live instances). */
  public reportToMaster() {
    if (this.masterId === undefined) return;
    this.sendToServer(this.masterId, [
      MASTER_AREA_PROTOCOLS.HOST_REPORT_AREAS,
      this.executor.report(),
    ]);
  }

  public async onServerRequest(server: BaseServerConfig<any>, payload: any): Promise<any> {
    if (server.type !== SERVER_TYPES.MASTER) return;
    switch (payload[0]) {
      case MASTER_AREA_PROTOCOLS.SPAWN_AREA:
        return this.executor.spawn(payload[1]);
      case MASTER_AREA_PROTOCOLS.TEARDOWN_AREA:
        return this.executor.destroy(payload[1], payload[2]);
    }
  }

  public onServerMessage(_server: BaseServerConfig<any>, _msg: any): void {
    // The agent is request/response only.
  }
}
